import api from "@/api";
import converters from "@/converters";
import type { ExternalGroupFragment } from "@/graphql";
import { Group_Connection } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { ICachedData } from "@/interfaces";
import { computed, ref } from "vue";

const DEFAULT_DATA: ICachedData<ExternalGroupFragment[]> = {
  status: "none",
  data: [],
};

const externalGroups = ref<
  Record<Group_Connection, ICachedData<ExternalGroupFragment[]>>
>({
  [Group_Connection.Activity]: { ...DEFAULT_DATA },
  [Group_Connection.Booking]: { ...DEFAULT_DATA },
  [Group_Connection.Document]: { ...DEFAULT_DATA },
  [Group_Connection.Gallery]: { ...DEFAULT_DATA },
  [Group_Connection.MealPlan]: { ...DEFAULT_DATA },
  [Group_Connection.News]: { ...DEFAULT_DATA },
  [Group_Connection.SchedulePlan]: { ...DEFAULT_DATA },
});

export function useExternalGroups(type: Group_Connection) {
  const init = async () => {
    const item = externalGroups.value[type];

    // We should fetch only once
    if (item.status === "loaded") {
      return;
    }

    item.status = "loading";
    const data = await api.groups.getExternalGroups(type);
    item.status = "loaded";
    item.data = data.institutions?.getMyInstitution.externalGroups || [];
  };

  init();

  const externalGroupList = computed(() => {
    return externalGroups.value[type].data.map(
      converters.externalGroups.toMultiSelectItem,
    );
  });

  const externalGroupOptions = computed(() => {
    return externalGroups.value[type].data.map(
      converters.externalGroups.toOption,
    );
  });

  const loading = computed(() => {
    return externalGroups.value[type].status === "loading";
  });

  const status = computed(() => {
    return externalGroups.value[type].status;
  });

  const isReady = () => isDataReady(status);

  return {
    externalGroups,
    externalGroupList,
    externalGroupOptions,
    loading,
    isReady,
  };
}
